import { useEffect, useMemo, useState } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { showcaseTableSelector } from '@app/store/selectors/showcase-table';
import tableDataFactory from '@app/pages/stepper/showcase/helpers/table-data-factory';
import {
  TABLE_ROWS_HIDDEN_IN_MARKETING_JOBS,
  TableRows,
  TABLE_ROWS_NAMES_LIST,
  TableRowsNames,
  TableRowsWithoutCompensation,
  TABLE_ROWS_NAMES_LIST_WITHOUT_COMPENSATION,
} from '@app/pages/stepper/showcase/constants/table-rows';
import { getCompensationPackageValues } from '@components/Organisms/GlobalCompensation/_helpers/bonuses-and-benefits';
import useDebounce from '@utils/hooks/use-debounce';
import { getApplicationsSortedByState } from '@app/pages/stepper/showcase/helpers/sort-candidates';
import { candidatesSelector } from '@model/Candidate/Candidate.selectors';
import {
  selectorJobArePayTransparencyRulesApplied,
  selectorJobIsReverseMarketing,
} from '@model/Job/Job.selectors';

const useShowcase = () => {
  const {
    rows,
    columns,
    areDiscardedVisible,
    areInterestedButVisible,
    isEditedByUser,
  } = useStoreState(showcaseTableSelector);
  const { results: candidates } = useStoreState(candidatesSelector);

  const { isConfigurationLoaded, isCompensationVisible } = useStoreState(
    showcaseTableSelector,
  );

  const isJobReverseMarketing = useStoreState(selectorJobIsReverseMarketing);

  const arePayTransparencyRulesApplied = useStoreState(
    selectorJobArePayTransparencyRulesApplied,
  );

  const { saveTableStatus, setIsEditedByUser } = useStoreActions(
    showcaseTableSelector,
  );

  const defaultRowsOrder = isCompensationVisible
    ? TableRows.map(row => row.id)
    : TableRowsWithoutCompensation.map(row => row.id);

  const autoSaveGuard = useDebounce(
    JSON.stringify({
      rows,
      columns,
      areDiscardedVisible,
      areInterestedButVisible,
    }),
    3000,
  );

  const [areColumnsHidden, setAreColumnsHidden] = useState(false);
  const [areRowsHidden, setAreRowsHidden] = useState(false);
  const [areRowsRearranged, setAreRowsRearranged] = useState(false);
  const [areColumnsRearranged, setAreColumnsRiarranged] = useState(false);

  const defaultApplicationsOrder = useMemo(() => {
    return getApplicationsSortedByState(candidates)?.map(application =>
      String(application.id),
    );
  }, [candidates]);

  const filteredRows = useMemo(() => {
    if (!candidates?.length) return [];

    const tableRowsNamesList = isCompensationVisible
      ? TABLE_ROWS_NAMES_LIST
      : TABLE_ROWS_NAMES_LIST_WITHOUT_COMPENSATION;

    const hasAnyCandidateWithCurrentCompensation = candidates.some(
      candidate =>
        getCompensationPackageValues({ candidate, isDesired: false }).total,
    );

    return rows.reduce((visibleRows, row) => {
      const isValidRow = tableRowsNamesList.includes(row.id);

      const isHiddenForMarketing =
        isJobReverseMarketing &&
        TABLE_ROWS_HIDDEN_IN_MARKETING_JOBS.includes(row.id);

      const isHiddenByPayTransparency =
        arePayTransparencyRulesApplied &&
        !hasAnyCandidateWithCurrentCompensation &&
        row.id === TableRowsNames.CURRENT_GLOBAL_COMPENSATION;

      if (
        !row.isVisible ||
        !isValidRow ||
        isHiddenForMarketing ||
        isHiddenByPayTransparency
      ) {
        return visibleRows;
      }

      return [
        ...visibleRows,
        {
          ...row,
          cells: tableDataFactory(
            arePayTransparencyRulesApplied,
            candidates,
            row,
          ),
        },
      ];
    }, []);
  }, [rows, candidates, isCompensationVisible, arePayTransparencyRulesApplied]);

  const filteredColumns = useMemo(() => {
    return columns.reduce(
      (visibleColumns, { isDiscarded, isInterestedBut, isVisible, id }) => {
        if (!areDiscardedVisible && isDiscarded) return visibleColumns;
        if (!areInterestedButVisible && isInterestedBut) return visibleColumns;
        if (!isVisible) return visibleColumns;

        const parsedId = String(id);

        return [
          ...visibleColumns,
          { id: parsedId, Header: parsedId, accessor: parsedId },
        ];
      },
      [
        {
          id: 'initial-column',
          Header: 'initial-column',
          accessor: 'initial-column',
        },
      ],
    );
  }, [columns, areDiscardedVisible, areInterestedButVisible]);

  const isRestoreViewButtonVisible = useMemo(() => {
    return (
      !!areColumnsHidden ||
      !!areRowsHidden ||
      areDiscardedVisible ||
      areInterestedButVisible ||
      areRowsRearranged ||
      areColumnsRearranged
    );
  }, [
    areColumnsHidden,
    areRowsHidden,
    areDiscardedVisible,
    areInterestedButVisible,
    areRowsRearranged,
    areColumnsRearranged,
  ]);

  useEffect(() => {
    return () => {
      setIsEditedByUser(false);
    };
  }, []);

  useEffect(() => {
    const currentRowsOrder = rows.map(row => row.id);
    setAreRowsHidden(rows.find(row => !row.isVisible));
    setAreRowsRearranged(
      JSON.stringify(defaultRowsOrder) !== JSON.stringify(currentRowsOrder),
    );
  }, [rows]);

  useEffect(() => {
    const currentColumnsOrder = columns.map(column => column.id);
    setAreColumnsHidden(!!columns.find(column => !column.isVisible));
    setAreColumnsRiarranged(
      JSON.stringify(defaultApplicationsOrder) !==
        JSON.stringify(currentColumnsOrder),
    );
  }, [columns]);

  useEffect(() => {
    if (isEditedByUser && isConfigurationLoaded) {
      saveTableStatus();
    }
  }, [autoSaveGuard]);

  return { filteredRows, filteredColumns, isRestoreViewButtonVisible };
};

export default useShowcase;
